import { PaginatedPoi } from './../../shared/models/paginated-poi.model';
import { ApiService } from './../../services/api.service';
import { LoggedUser } from './../../core/logged/logged.model';
import { Component, OnInit, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { tap, first, switchMap, startWith, map, catchError } from 'rxjs/operators';
import { Observable, Subscription, of } from 'rxjs';
import { IPoi } from '../../core/poi/poi.model';
import { select } from '@angular-redux/store';
import { TranslateService } from '@ngx-translate/core';
import { PoiActionService } from 'src/app/core/poi/poi.action';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
  selector: 'app-lista-poi',
  templateUrl: './lista-poi.component.html',
  styleUrls: ['./lista-poi.component.scss']
})
export class ListaPoiComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumns: string[];
  dataSource: MatTableDataSource<IPoi>;
  columnDefinitions = {
    mobile: ['poi_name'],
    desktop: ['poi_name', 'address']
  };
  current_lang = 'it';

  @select(['poi', 'list'])
  poi_list$: Observable<any>;
  @select(['poi', 'total'])
  poi_list_total$: Observable<any>;
  poi_list_total: number = 0;
  @select(['poi', 'page'])
  poi_list_page$: Observable<any>;
  poi_list_page: number = 0;
  @select(['poi', 'size'])
  poi_list_size$: Observable<any>;
  poi_list_size: number = 0;

  @select(['logged', 'logged'])
  user_logged$: Observable<any>;
  private userGeoPosId: number;

  private searchString: string = '';

  private subscriptions: Subscription[] = [];

  loading = false;
  loadingPoi = false;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  constructor(
    private titleService: Title,
    private translate: TranslateService,
    private poiAction: PoiActionService,
    private breakpointObserver: BreakpointObserver,
    private api: ApiService
  ) {
    this.translate.setDefaultLang('it');
    if (this.translate.currentLang) {
      this.current_lang = this.translate.currentLang;
      this.translate.use(this.current_lang);
    } else {
      this.translate.use('it');
      this.current_lang = this.translate.currentLang;
    }
    this.translate.get('breadcrumb.admin_poi').subscribe((res: string) => {
      this.titleService.setTitle(res + ' - PRIMIS');
    });
    this.translate.onLangChange.subscribe(() => {
      this.translate.get('breadcrumb.admin_poi').subscribe((res: string) => {
        this.titleService.setTitle(res + ' - PRIMIS');
      });
      this.changeLangFunction();
    });
  }

  ngOnInit() {
    this.user_logged$.pipe(
      first(),
      tap((user: LoggedUser) => {
        this.userGeoPosId = user.auth_user === 'superadmin' ? null : user.geopos[0].id;
      })).subscribe();

    this.subscriptions.push(
      this.poi_list_total$.subscribe((total) => {
        this.poi_list_total = total;
      }),
      this.poi_list_size$.subscribe((size) => {
        this.poi_list_size = size;
      }),
      this.breakpointObserver.observe('(max-width: 800px)').subscribe(result => {
        if (result.matches) {
          this.displayedColumns = this.columnDefinitions.mobile;
        } else {
          this.displayedColumns = this.columnDefinitions.desktop;
        }
      })
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.paginator.page.pipe(
        startWith({}),
        switchMap(() => {
          const pageIndex = !!this.paginator.pageIndex ? this.paginator.pageIndex : 0;
          this.loading = true;
          return this.api.getPoi(
            pageIndex,
            this.userGeoPosId,
            null,
            this.searchString,
            this.current_lang,
            true
          );
        }),
        map((paginatedPoi: PaginatedPoi) => {
          this.dataSource = new MatTableDataSource(paginatedPoi.list);
          this.poiAction.paginatedPoiList(paginatedPoi);
          this.loading = false;
        }),
        catchError((err) => {
          console.error('Non è stato possibile recuperare la lista di POI');
          this.loading = false;
          return of(err);
        })
      ).subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  applyFilter(event: Event) {
    const searchPoiLength = this.searchString.length;
    this.searchString = this.getSearchString((event.target as HTMLInputElement).value);
    if (searchPoiLength !== this.searchString.length) {
      this.paginator.firstPage();
      this.loadingPoi = true;
      this.api.getPoi(
        0,
        this.userGeoPosId,
        null,
        this.searchString,
        this.current_lang,
        true
      ).pipe(
        first(),
        tap((paginatedPoi: PaginatedPoi) => {
          this.dataSource = new MatTableDataSource(paginatedPoi.list);
          this.poiAction.paginatedPoiList(paginatedPoi);
          this.loadingPoi = false;
        }),
        catchError((err) => {
          console.error('Non è stato possibile recuperare la lista di POI');
          this.loadingPoi = false;
          return of(err);
        })
      ).subscribe();
    }
  }

  private getSearchString(searchString: string): string {
    if (!searchString || searchString?.length < 4) {
      return '';
    }
    return searchString;
  }

  changeLangFunction(): void {
    this.current_lang = this.translate.currentLang;
    this.translate.get('admin_poi.paginator').subscribe(translation => {
      this.paginator._intl.itemsPerPageLabel = translation.items_per_page;
      this.paginator._intl.nextPageLabel = translation.next;
      this.paginator._intl.previousPageLabel = translation.prev;
      this.paginator._intl.getRangeLabel = function(page, pageSize, length) {
        const of = translation.elem_of_tot;
        if (length === 0 || pageSize === 0) {
          return '0 ' + of + ' ' + length;
        }
        length = Math.max(length, 0);
        const startIndex = page * pageSize;
        // If the start index exceeds the list length, do not try and fix the end index to the end.
        const endIndex = startIndex < length ?
          Math.min(startIndex + pageSize, length) :
          startIndex + pageSize;
        return startIndex + 1 + ' - ' + endIndex + ' ' + of + ' ' + length;
      };
    });
  }
}


